import { useState } from 'react'

import {
  AvatarStack,
  Box,
  CollapsableCard,
  Icon,
  Text,
} from '@factorialco/deprecated-design-system'
import { RichTextDisplay } from '@factorialco/factorial-one-react/dist/experimental'
import {
  AtsGetInterviewAttendeesDocument,
  AtsInterviewAttendeeFragment,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { convertDate, dateBackendDateTimeToUITime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { timezoneToString } from 'modules/core/lib/timezones'

import { CreateInterviewData } from './CreateInterviewData'

interface Props {
  data: CreateInterviewData
  onDataChanged: (data: CreateInterviewData) => void
}

export const isReviewCompleted = () => true

export const ReviewStep = ({ data }: Props) => {
  const [showMessage, setShowMessage] = useState(false)

  const { data: attendeesData } = useQuery(AtsGetInterviewAttendeesDocument, {
    variables: { attendeeIds: data.attendeeIds },
  })

  const attendees = attendeesData?.apiCore.accessesConnection.nodes || []

  const avatars = attendees.map((attendee: AtsInterviewAttendeeFragment) => {
    return {
      label: attendee.fullName,
      url: buildUrl(attendee.avatarFull),
    }
  })

  return (
    <Box gap="s24">
      <Box
        gap="s12"
        padding="s16"
        borderRadius={{ all: 'abs008' }}
        border={{ all: { style: 'solid', width: 's1', color: 'grey400' } }}
      >
        <Box flexDirection="row" gap="s16" alignSelf="flexStart">
          <Text.Normal weight="semibold">
            {i18n.t('ats.schedule_interview.steps.review.attendees')}
          </Text.Normal>
          <AvatarStack
            size="s24"
            leftover={0}
            backgroundColor="grey000White"
            textSize="200"
            textColor="grey1000"
            avatars={avatars}
          />
        </Box>
        <Box flexDirection="row" gap="s16">
          <Text.Normal weight="semibold">
            {i18n.t('ats.schedule_interview.steps.review.date')}
          </Text.Normal>
          {data.date && (
            <Text.Normal>{convertDate(data.date, 'backend_datetime', 'ui_long')}</Text.Normal>
          )}
        </Box>
        <Box flexDirection="row" gap="s16">
          <Text.Normal weight="semibold">
            {i18n.t('ats.schedule_interview.steps.review.time')}
          </Text.Normal>
          {data.date && <Text.Normal>{dateBackendDateTimeToUITime(data.date)}</Text.Normal>}
          <Text.Normal>{timezoneToString(data.timezone)}</Text.Normal>
        </Box>
        <Box flexDirection="row" gap="s16">
          <Text.Normal weight="semibold">
            {i18n.t('ats.schedule_interview.steps.review.duration')}
          </Text.Normal>
          <Text.Normal>
            {i18n.t('ats.schedule_interview.steps.review.minutes', { minutes: data.minutes })}
          </Text.Normal>
        </Box>
        {data.customLocation && (
          <Box flexDirection="row" gap="s16">
            <Text.Normal weight="semibold">
              {i18n.t('ats.schedule_interview.steps.review.location')}
            </Text.Normal>
            <Text.Normal>{data.customLocation}</Text.Normal>
          </Box>
        )}
      </Box>
      {!!data.messageContent && (
        <CollapsableCard
          open={showMessage}
          Card={
            <Box flexDirection="row" onClick={() => setShowMessage(!showMessage)}>
              <Box flexGrow>
                <Text.Normal weight="semibold">
                  {i18n.t('ats.schedule_interview.steps.review.message')}
                </Text.Normal>
              </Box>
              <Text.Normal weight="semibold">
                <Icon.Medium icon={showMessage ? 'chevron-down' : 'chevron-left'} />
              </Text.Normal>
            </Box>
          }
          Content={
            <Box>
              <Text.Header3>{data.subject}</Text.Header3>
              <RichTextDisplay content={data.messageContent} />
            </Box>
          }
        />
      )}
    </Box>
  )
}
